/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Answer Utilities - Shared functions for handling answer options
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

/**
 * Normalize any answer shape to a plain display string.
 *
 * Schema v2 stores answers as objects: `{ value: "27556" }` for a single answer or
 * `{ values: ["A", "B"] }` for multi-select. Older data may be a bare string or array. JSX must
 * never receive the raw object (React throws "Objects are not valid as a React child"), so every
 * answer is run through here before rendering.
 *
 * @param {*} answer - The answer value (string, number, array, or { value } / { values } object)
 * @returns {string} A display-safe string ('' when empty)
 */
export const getAnswerValue = (answer) => {
  if (answer === null || answer === undefined) return ''

  if (typeof answer === 'string' || typeof answer === 'number') {
    return String(answer)
  }

  if (Array.isArray(answer)) {
    return answer.map(getAnswerValue).filter(Boolean).join(', ')
  }

  if (typeof answer === 'object') {
    if (Array.isArray(answer.values)) {
      return answer.values.map(getAnswerValue).filter(Boolean).join(', ')
    }
    const candidate = answer.value ?? answer.answer ?? answer.key ?? answer.choice
    if (candidate !== undefined && candidate !== null) {
      return getAnswerValue(candidate)
    }
    return ''
  }

  return String(answer)
}

/**
 * Get the display label for an answer (e.g., "A", "B", "C", "D")
 * @param {*} answer - The answer value (could be string, number, array)
 * @returns {string} The formatted answer label
 */
export const getAnswerLabel = (answer) => {
  if (answer === null || answer === undefined) return '-'

  // Handle array answers (e.g., ["A"] from correct_answer)
  if (Array.isArray(answer)) {
    return answer.length > 0 ? getAnswerLabel(answer[0]) : '-'
  }

  // Convert to string if number or other type
  const answerStr = String(answer)

  // Handle letter answers (A, B, C, D)
  if (answerStr.length === 1 && /[A-D]/i.test(answerStr)) {
    return answerStr.toUpperCase()
  }

  return answerStr
}

/**
 * Get the content text for a specific answer option
 * @param {Array|Object} answerOptions - The answer options (array or object format)
 * @param {string} answerLabel - The answer label to look up (e.g., "A", "B")
 * @returns {string|null} The answer option content or null if not found
 */
export const getAnswerOptionContent = (answerOptions, answerLabel) => {
  if (!answerOptions || !answerLabel) return null

  const labelUpper = String(answerLabel).toUpperCase()
  const labelLower = String(answerLabel).toLowerCase()

  // Handle array format: [{ label: "A", text: "..." }, ...]
  if (Array.isArray(answerOptions)) {
    const option = answerOptions.find(
      (opt) => opt?.label?.toUpperCase() === labelUpper || opt?.key?.toUpperCase() === labelUpper
    )
    if (option) {
      return option.text || option.content || option.value || null
    }
    return null
  }

  // Handle object format: { A: "...", B: "..." }
  if (typeof answerOptions === 'object' && answerOptions !== null) {
    return answerOptions[labelUpper] || answerOptions[labelLower] || null
  }

  return null
}

/**
 * Get the label for an answer option at a specific index
 * @param {Object} option - The answer option object
 * @param {number} index - The index of the option
 * @returns {string} The option label
 */
export const getOptionLabel = (option, index) => {
  if (option?.label) return String(option.label)
  if (option?.key) return String(option.key)
  return String.fromCharCode(65 + index) // A, B, C, D...
}

/**
 * Get the text content from an answer option
 * @param {Object|string} option - The answer option
 * @returns {string} The option text content
 */
export const getOptionText = (option) => {
  if (typeof option === 'string') return option
  if (option?.text) return String(option.text)
  if (option?.content) return String(option.content)
  if (option?.value) return String(option.value)
  return ''
}

/**
 * Check if an option matches a given answer label
 * @param {Object} option - The answer option
 * @param {number} index - The index of the option
 * @param {string} answerLabel - The answer label to compare
 * @returns {boolean} True if the option matches the answer label
 */
export const isOptionMatch = (option, index, answerLabel) => {
  if (!answerLabel) return false
  const optionLabel = getOptionLabel(option, index)
  return optionLabel.toUpperCase() === String(answerLabel).toUpperCase()
}
